import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import { profileApi, profileQueryKeys, type Profile } from '@entities/profile';
import { showToast } from '@features/ToastManager';

import { PROFILE_TOAST_SCOPE } from './toast';
import { copyTextToClipboard } from '../lib/copyTextToClipboard';

import type { IDataResponseError } from '@shared/model/types/dataResponse.inteface';

export const useApiTokenSettings = (profile: Profile) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const apiToken = profile.apiToken.value;
    const mutation = useMutation({
        mutationFn: () => (apiToken ? profileApi.refreshApiToken() : profileApi.createApiToken()),
        onSuccess: (token) => {
            queryClient.setQueryData<Profile>(profileQueryKeys.current(), (current) =>
                current ? { ...current, apiToken: { ...current.apiToken, value: token } } : current
            );
            showToast(t('profile.page.settings.api.success'), { scope: PROFILE_TOAST_SCOPE });
        },
        onError: (error) => {
            const responseError = isAxiosError<IDataResponseError>(error)
                ? error.response?.data
                : undefined;
            showToast(
                responseError?.message ?? t('profile.page.settings.api.errors.generate'),
                { scope: PROFILE_TOAST_SCOPE, type: 'error' }
            );
        },
    });

    const copyToken = async () => {
        if (!apiToken) {
            return;
        }
        try {
            await copyTextToClipboard(apiToken);
            showToast(t('profile.page.settings.api.copySuccess'), {
                scope: PROFILE_TOAST_SCOPE,
            });
        } catch {
            showToast(t('profile.page.settings.api.errors.copy'), {
                scope: PROFILE_TOAST_SCOPE,
                type: 'error',
            });
        }
    };

    return {
        apiToken,
        canUpdate: profile.apiToken.canUsePublicApiByTariff,
        updateToken: mutation.mutate,
        copyToken,
        isPending: mutation.isPending,
    };
};
